/**
 * Frame step panel components for the 3-panel layout.
 * Step 2 of the edit flow: canvas choice (left), preview (center), frame list (right).
 */

import { FrameLayoutCard } from './FrameLayoutCard';
import { InteractiveCanvasPreview } from './InteractiveCanvasPreview';
import { getLayoutById, getAspectRatioForLayout } from '../types/canvas-layouts';
import type { LocalFrame } from '../../../lib/db/schema';
import type { EditPhotoMinimal, PhotoTransform } from '../types/edit-session';
import { sortFramesByCategory, groupFramesByCategory } from '../utils/frame-sorting';

export function getSlotCount(paperType: 'collage_4x6' | 'strip'): number {
  return paperType === 'collage_4x6' ? 4 : 2;
}

/** Left panel: canvas tabs and frame count */
export interface FrameStepCanvasPanelProps {
  printCount: number;
  currentCanvasIndex: number;
  selectedFrameIds: string[];
  onCanvasChange: (canvasIndex: number) => void;
  frameCount: number;
}

export function FrameStepCanvasPanel({
  printCount,
  currentCanvasIndex,
  selectedFrameIds,
  onCanvasChange,
  frameCount,
}: FrameStepCanvasPanelProps) {
  return (
    <div className="flex flex-col h-full" role="region" aria-label="Photo selection">
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Choose frame for</h2>
      <p className="text-sm text-gray-600 mb-3">Select a photo, then pick a frame.</p>
      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full self-start mb-4">
        {frameCount} frame{frameCount !== 1 ? 's' : ''} available
      </span>
      {printCount > 1 ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: printCount }, (_, i) => (
            <button
              key={i}
              onClick={() => onCanvasChange(i)}
              className={`px-4 py-3 text-sm font-medium rounded-lg border-2 text-left transition-colors ${
                i === currentCanvasIndex
                  ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
              data-testid={`canvas-frame-tab-${i}`}
            >
              Photo {i + 1}
              {selectedFrameIds[i] && (
                <span className="ml-1 text-xs bg-green-100 text-green-600 px-1 rounded">✓</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-600">Photo 1</p>
      )}
    </div>
  );
}

/** Center panel: selected frame preview and canvas preview with user photos */
export interface FrameStepPreviewPanelProps {
  selectedFrame: LocalFrame | null;
  currentCanvasIndex: number;
  layoutId: string | undefined;
  /** Global slot index offset for the current canvas (same as step 1). */
  slotOffset: number;
  photoArrangement: Map<number, string>;
  photos: EditPhotoMinimal[];
  paperType: 'collage_4x6' | 'strip';
  photoTransforms: Map<number, PhotoTransform>;
  onTransformChange: (globalSlotIndex: number, transform: PhotoTransform) => void;
}

export function FrameStepPreviewPanel({
  selectedFrame,
  currentCanvasIndex,
  layoutId,
  slotOffset,
  photoArrangement,
  photos,
  photoTransforms,
  onTransformChange,
}: FrameStepPreviewPanelProps) {
  const layout = layoutId ? getLayoutById(layoutId) : undefined;
  const paperAspectRatio = layout ? getAspectRatioForLayout(layout) : '2/3';

  return (
    <div className="flex flex-col h-full min-h-0" role="region" aria-label="Frame preview">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Preview</h2>
      {layoutId ? (
        <div
          className={`p-4 rounded-xl flex flex-col min-h-0 flex-1 ${
            selectedFrame ? 'bg-gray-50 border-2 border-yellow-500/50' : 'bg-gray-50 border-2 border-dashed border-gray-200'
          }`}
          data-testid={selectedFrame ? 'selected-frame-preview' : 'no-frame-preview'}
          role="status"
          aria-label={selectedFrame ? `Selected frame: ${selectedFrame.name}` : 'Photos without frame'}
        >
          <div className="mt-3 flex-1 min-h-0 flex flex-col overflow-auto">
            {/* Same aspect-ratio sizing as step 1 center panel: paper fits in panel and keeps layout aspect ratio */}
            <div
              className="flex items-center justify-center min-h-0 flex-1 w-full"
              style={{ minHeight: 0 }}
            >
              <div
                className="relative flex-shrink-0 bg-gray-50 rounded border border-gray-200"
                style={{
                  aspectRatio: paperAspectRatio,
                  height: '100%',
                  width: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
                <InteractiveCanvasPreview
                  canvasIndex={currentCanvasIndex}
                  layoutId={layoutId}
                  slotOffset={slotOffset}
                  photoArrangement={photoArrangement}
                  photos={photos}
                  frame={selectedFrame}
                  photoTransforms={photoTransforms}
                  onTransformChange={onTransformChange}
                  className="w-full h-full max-w-full max-h-full"
                />
              </div>
            </div>
          </div>
          {!selectedFrame && (
            <p className="text-center text-sm text-gray-500 mt-2">
              No frame selected. Pick a frame below to add one, or click Complete to keep as is.
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 text-gray-400 text-sm">
          No layout selected
        </div>
      )}
    </div>
  );
}

/** Right panel: one row per category, each row scrollable to the right (image only, no frame name) */
export interface FrameStepFrameListPanelProps {
  frames: LocalFrame[];
  /** Map category id -> display name from frame categories API (e.g. "General") */
  frameCategoryNames?: Record<string, string>;
  /** Map category id -> displayOrder for ordering category rows (lower first) */
  frameCategoryDisplayOrder?: Record<string, number>;
  paperType: 'collage_4x6' | 'strip';
  currentSelectedFrameId: string;
  currentCanvasIndex: number;
  onFrameSelect: (frameId: string, canvasIndex: number) => void;
}

export function FrameStepFrameListPanel({
  frames,
  frameCategoryNames = {},
  frameCategoryDisplayOrder = {},
  paperType,
  currentSelectedFrameId,
  currentCanvasIndex,
  onFrameSelect,
}: FrameStepFrameListPanelProps) {
  const sorted = sortFramesByCategory(frames);
  const groups = groupFramesByCategory(sorted);
  // Order category rows by API displayOrder (lower first); unknown categories last, then by categoryId
  const orderedCategoryEntries = Array.from(groups.entries()).sort(([idA], [idB]) => {
    const orderA = frameCategoryDisplayOrder[idA] ?? Infinity;
    const orderB = frameCategoryDisplayOrder[idB] ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;
    return idA.localeCompare(idB);
  });

  const isNoFrameSelected = !currentSelectedFrameId || currentSelectedFrameId === '';

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid="frame-category-selector"
      role="region"
      aria-label="Frame list"
    >
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Frames</h2>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* No frame option: allows user to remove the selected frame */}
        <div className="mb-4" data-testid="frame-option-no-frame">
          <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Option</h3>
          <div className="flex flex-row gap-3">
            <button
              type="button"
              onClick={() => onFrameSelect('', currentCanvasIndex)}
              className={`
                relative flex-shrink-0 w-20 h-20 rounded-lg border-2 transition-all duration-200
                flex items-center justify-center text-xs font-medium
                focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
                ${isNoFrameSelected
                  ? 'border-yellow-500 bg-yellow-50 text-yellow-800 shadow-md'
                  : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-yellow-400 hover:bg-yellow-50/50'
                }
              `}
              data-testid="frame-no-frame"
              aria-pressed={isNoFrameSelected}
              aria-label="No frame"
            >
              <span className="text-center px-1">No frame</span>
              {isNoFrameSelected && (
                <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500 text-white" aria-hidden>
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-12 text-gray-400"
            data-testid="frame-category-empty"
          >
            <p className="text-center text-sm">
              No frames available for {paperType === 'collage_4x6' ? '4x6 Collage' : 'Strip'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="frame-category-groups">
            {orderedCategoryEntries.map(([categoryId, categoryFrames]) => (
              <div key={categoryId} data-testid={`frame-category-${categoryId}`}>
                <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
                  {frameCategoryNames[categoryId] ?? `Category ${categoryId.slice(0, 8)}`}
                </h3>
                <div className="overflow-x-auto overflow-y-hidden pb-2">
                  <div
                    className="flex flex-row gap-3"
                    style={{ width: 'max-content' }}
                    data-testid="frame-list"
                  >
                    {categoryFrames.map((frame) => (
                      <FrameLayoutCard
                        key={frame.frameId}
                        frame={frame}
                        isSelected={frame.frameId === currentSelectedFrameId}
                        onSelect={(frameId) => onFrameSelect(frameId, currentCanvasIndex)}
                        compact
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
